import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Loader2, Truck, MapPin, ChevronRight, SlidersHorizontal, X, ArrowUp, ArrowLeft } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import QuickView from "../components/QuickView";
import { consumeBackNavigation, restoreScrollPosition, saveScrollPosition } from "../components/ScrollToTop";
import PersistentDateBar from "../components/PersistentDateBar";
import TierDiscountStrip from "../components/TierDiscountStrip";
import { useBooking } from "../contexts/BookingContext";
import { useSiteTexts, t } from "../contexts/SiteTextsContext";
import SEO from "../components/SEO";
import { useBuildLook } from "../contexts/BuildLookContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Catalog() {
  const { parentSlug, childSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [outfits, setOutfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [availability, setAvailability] = useState({});
  const [categoryTree, setCategoryTree] = useState([]);
  const { eventDate, rentalDuration, hasDateSelected } = useBooking();
  const texts = useSiteTexts();
  const [quickViewOutfit, setQuickViewOutfit] = useState(null);
  
  // Build Look mode
  const { isActive: isBuildLookActive } = useBuildLook();
  const buildLookParam = searchParams.get("buildLook") === "true";
  const slotParam = searchParams.get("slot");
  const inBuildLookMode = buildLookParam || isBuildLookActive;

  // Filter state
  const [availableColours, setAvailableColours] = useState([]);
  const [availableEventTypes, setAvailableEventTypes] = useState([]);
  const [selectedColours, setSelectedColours] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [allColours, setAllColours] = useState([]);
  const [allEventTypes, setAllEventTypes] = useState([]);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const queryCategory = searchParams.get("category");
  const querySearch = searchParams.get("search");

  useEffect(() => {
    if (querySearch) setSearchTerm(querySearch);
  }, [querySearch]);
  const didRestoreScroll = useRef(false);

  useEffect(() => {
    fetch(`${API}/settings/categories`).then(r => r.json()).then(d => setCategoryTree(d.categories || [])).catch(() => {});
    fetch(`${API}/settings/product-colours`).then(r => r.json()).then(d => setAllColours(d.colours || [])).catch(() => {});
    fetch(`${API}/settings/event-types`).then(r => r.json()).then(d => setAllEventTypes(d.event_types || [])).catch(() => {});
    fetchOutfits();
  }, []);

  // Restore scroll position after outfits load (on back navigation)
  useEffect(() => {
    if (!loading && outfits.length > 0 && !didRestoreScroll.current) {
      didRestoreScroll.current = true;
      if (consumeBackNavigation()) {
        restoreScrollPosition("/catalog");
      }
    }
  }, [loading, outfits.length]);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (eventDate) {
      fetchAvailability();
    } else {
      setAvailability({});
    }
  }, [eventDate, rentalDuration]);

  const fetchOutfits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/outfits`);
      setOutfits(response.data);
    } catch (error) {
      console.error("Error fetching outfits:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    if (!eventDate) return;
    try {
      const y = eventDate.getFullYear();
      const m = String(eventDate.getMonth() + 1).padStart(2, "0");
      const d = String(eventDate.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;
      
      const [res4, res8] = await Promise.all([
        axios.get(`${API}/outfits/check-availability?event_date=${dateStr}&rental_days=4`),
        axios.get(`${API}/outfits/check-availability?event_date=${dateStr}&rental_days=8`)
      ]);
      
      const avail4 = res4.data.availability || {};
      const avail8 = res8.data.availability || {};
      
      const combined = {};
      const allIds = new Set([...Object.keys(avail4), ...Object.keys(avail8), ...outfits.map(o => o.id)]);
      
      allIds.forEach(id => {
        const is4Available = avail4[id] !== false;
        const is8Available = avail8[id] !== false;
        
        let status;
        if (rentalDuration) {
          if (rentalDuration === 8) {
            status = is8Available ? 'available' : (is4Available ? 'limited' : 'unavailable');
          } else {
            status = is4Available ? 'available' : (is8Available ? 'limited' : 'unavailable');
          }
        } else {
          status = (is4Available || is8Available) ? 'available' : 'unavailable';
        }
        
        combined[id] = { avail4: is4Available, avail8: is8Available, status };
      });
      
      setAvailability(combined);
    } catch (err) {
      console.error("Error checking availability:", err);
    }
  };

  // Category resolution
  const enabledParents = categoryTree.filter(c => c.enabled !== false);

  const resolveContext = () => {
    let parent = null, child = null, activeFilter = "all";
    if (parentSlug) {
      parent = enabledParents.find(c => (c.slug || c.id) === parentSlug);
      if (parent && childSlug) {
        const enabledChildren = (parent.children || []).filter(c => c.enabled !== false);
        child = enabledChildren.find(c => (c.slug || c.id) === childSlug);
        if (child) activeFilter = child.slug || child.id;
      }
    } else if (queryCategory) {
      for (const p of enabledParents) {
        if ((p.slug || p.id) === queryCategory) { parent = p; break; }
        const enabledChildren = (p.children || []).filter(c => c.enabled !== false);
        const found = enabledChildren.find(c => (c.slug || c.id) === queryCategory);
        if (found) { parent = p; child = found; activeFilter = found.slug || found.id; break; }
      }
    }
    return { parent, child, activeFilter };
  };

  const { parent, child, activeFilter } = resolveContext();
  const filterTabs = parent?.children?.filter(c => c.enabled !== false) || [];
  const pageTitle = child ? child.name : parent ? parent.name : t(texts, "catalog_heading_title", "Our Collection");
  const buildCategoryUrl = (parentCat, childCat) => {
    if (childCat) return `/${parentCat.slug || parentCat.id}/${childCat.slug || childCat.id}`;
    if (parentCat) return `/${parentCat.slug || parentCat.id}`;
    return "/catalog";
  };

  // Filter outfits
  const filteredOutfits = useMemo(() => {
    return outfits.filter(outfit => {
      if (!outfit.available) return false;

      // Hide unavailable when date is selected
      if (hasDateSelected && availability[outfit.id]?.status === 'unavailable') return false;

      const matchesSearch = !searchTerm ||
        outfit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (outfit.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (outfit.category || "").toLowerCase().includes(searchTerm.toLowerCase());

      let matchesCategory = true;
      if (activeFilter !== "all") {
        const outfitCats = [outfit.category, ...(outfit.categories || []), ...(outfit.subcategories || [])];
        matchesCategory = outfitCats.some(c => c === activeFilter || c === (child?.name));
      } else if (parent && !child) {
        const parentSlugs = new Set([parent.slug || parent.id, parent.name]);
        (parent?.children || []).forEach(s => { parentSlugs.add(s.slug || s.id); parentSlugs.add(s.name); });
        const outfitCats = [outfit.category, ...(outfit.categories || []), ...(outfit.subcategories || [])];
        matchesCategory = outfitCats.some(c => parentSlugs.has(c));
      }

      // Colour filter (OR within group)
      const matchesColour = selectedColours.length === 0 || 
        selectedColours.includes(outfit.color);

      // Event Type filter (OR within group)
      const matchesEventType = selectedEventTypes.length === 0 || 
        (outfit.event_types || []).some(et => selectedEventTypes.includes(et));

      return matchesSearch && matchesCategory && matchesColour && matchesEventType;
    });
  }, [outfits, searchTerm, activeFilter, parent, child, hasDateSelected, availability, selectedColours, selectedEventTypes]);

  // Customer-facing sort control — shared across every page that renders
  // this Catalog component (For Her, Accessories, occasions, categories,
  // search). Uses the LOWEST starting rental price for price sorts, ignoring
  // bond entirely, and never mutates catalogue records.
  const [sortBy, setSortBy] = useState(() => {
    try { return sessionStorage.getItem("catalog_sort_by") || "featured"; } catch (_) { return "featured"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("catalog_sort_by", sortBy); } catch (_) { /* ignore */ }
  }, [sortBy]);

  const getStartingPrice = (o) => {
    const prices = [o.price_4_day, o.price_8_day, o.price_per_day,
      ...(o.rental_options || []).flatMap(r => [r.price_4_day, r.price_8_day, r.price_per_day])]
      .map(Number).filter(p => p > 0);
    return prices.length ? Math.min(...prices) : Infinity;
  };
  const getCreatedTs = (o) => {
    const t = o.created_at || o.updated_at || o.createdAt;
    const d = t ? new Date(t).getTime() : 0;
    return isNaN(d) ? 0 : d;
  };

  const sortedOutfits = useMemo(() => {
    if (sortBy === "featured") return filteredOutfits;
    const arr = [...filteredOutfits];
    switch (sortBy) {
      case "newest":     arr.sort((a, b) => getCreatedTs(b) - getCreatedTs(a)); break;
      case "price_asc":  arr.sort((a, b) => getStartingPrice(a) - getStartingPrice(b)); break;
      case "price_desc": arr.sort((a, b) => getStartingPrice(b) - getStartingPrice(a)); break;
      case "name_asc":   arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
      case "name_desc":  arr.sort((a, b) => (b.name || "").localeCompare(a.name || "")); break;
      default: break;
    }
    return arr;
  }, [filteredOutfits, sortBy]);

  // Compute available colours & event types based on outfits in current category (before colour/event filters)
  useEffect(() => {
    const categoryOutfits = outfits.filter(outfit => {
      if (!outfit.available) return false;
      if (hasDateSelected && availability[outfit.id]?.status === 'unavailable') return false;
      let matchesCategory = true;
      if (activeFilter !== "all") {
        const outfitCats = [outfit.category, ...(outfit.categories || []), ...(outfit.subcategories || [])];
        matchesCategory = outfitCats.some(c => c === activeFilter || c === (child?.name));
      } else if (parent && !child) {
        const parentSlugs = new Set([parent.slug || parent.id, parent.name]);
        (parent?.children || []).forEach(s => { parentSlugs.add(s.slug || s.id); parentSlugs.add(s.name); });
        const outfitCats = [outfit.category, ...(outfit.categories || []), ...(outfit.subcategories || [])];
        matchesCategory = outfitCats.some(c => parentSlugs.has(c));
      }
      return matchesCategory;
    });

    const coloursInCategory = new Set(categoryOutfits.map(o => o.color).filter(Boolean));
    setAvailableColours(allColours.filter(c => coloursInCategory.has(c)));

    const eventTypesInCategory = new Set(categoryOutfits.flatMap(o => o.event_types || []));
    setAvailableEventTypes(allEventTypes.filter(et => eventTypesInCategory.has(et.name)));

    // Clear selections that are no longer valid — only if something actually changed
    setSelectedColours(prev => {
      const filtered = prev.filter(c => coloursInCategory.has(c));
      return filtered.length === prev.length ? prev : filtered;
    });
    setSelectedEventTypes(prev => {
      const filtered = prev.filter(et => eventTypesInCategory.has(et));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [outfits, activeFilter, parent, child, hasDateSelected, availability, allColours, allEventTypes]);

  const toggleColour = (colour) => {
    setSelectedColours(prev => prev.includes(colour) ? prev.filter(c => c !== colour) : [...prev, colour]);
  };

  const toggleEventType = (et) => {
    setSelectedEventTypes(prev => prev.includes(et) ? prev.filter(e => e !== et) : [...prev, et]);
  };

  const clearAllFilters = () => {
    setSelectedColours([]);
    setSelectedEventTypes([]);
    setSearchTerm("");
  };

  const hasActiveFilters = selectedColours.length > 0 || selectedEventTypes.length > 0;

  // Filter sidebar component
  const FilterPanel = ({ isMobile = false }) => (
    <div className={isMobile ? "p-5 space-y-8" : "space-y-8 pr-6"}>
      {/* Colour Filter */}
      {availableColours.length > 0 && (
        <div data-testid="colour-filter">
          <h4 className="text-[12px] tracking-[0.1em] uppercase text-[#282828] font-medium mb-4">Colour</h4>
          <div className="space-y-2">
            {availableColours.map(colour => (
              <label key={colour} className="flex items-center gap-2.5 cursor-pointer group" data-testid={`colour-option-${colour}`}>
                <div className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center transition-colors ${
                  selectedColours.includes(colour) ? 'border-[#9A7B4F] bg-[#9A7B4F]' : 'border-[#282828]/20 group-hover:border-[#282828]/40'
                }`}>
                  {selectedColours.includes(colour) && (
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  )}
                </div>
                <span className="text-[13px] text-[#5A5A5A]">{colour}</span>
                <input type="checkbox" className="sr-only" checked={selectedColours.includes(colour)} onChange={() => toggleColour(colour)} />
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Event Type Filter */}
      {availableEventTypes.length > 0 && (
        <div data-testid="event-type-filter" className="pt-6 border-t border-[#282828]/10">
          <h4 className="text-[12px] tracking-[0.1em] uppercase text-[#282828] font-medium mb-4">Event Type</h4>
          <div className="space-y-2">
            {availableEventTypes.map(et => (
              <label key={et.id} className="flex items-center gap-2.5 cursor-pointer group" data-testid={`event-type-option-${et.id}`}>
                <div className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center transition-colors ${
                  selectedEventTypes.includes(et.name) ? 'border-[#9A7B4F] bg-[#9A7B4F]' : 'border-[#282828]/20 group-hover:border-[#282828]/40'
                }`}>
                  {selectedEventTypes.includes(et.name) && (
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  )}
                </div>
                <span className="text-[13px] text-[#5A5A5A]">{et.name}</span>
                <input type="checkbox" className="sr-only" checked={selectedEventTypes.includes(et.name)} onChange={() => toggleEventType(et.name)} />
              </label>
            ))}
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <button onClick={clearAllFilters} className="text-[12px] text-[#9A7B4F] hover:text-[#8a6b3f] transition-colors underline underline-offset-2" data-testid="clear-filters-btn">
          Clear all filters
        </button>
      )}
    </div>
  );

  // SEO
  const seoTitle = child ? child.name : parent ? parent.name : (parentSlug === "accessories" ? "Accessories" : parentSlug === "for-her" ? "For Her" : "Our Collection");
  const seoDesc = parent
    ? `Rent ${seoTitle} in Melbourne. Designer Indian ${seoTitle.toLowerCase()} for hire. Luxury looks without the luxury price.`
    : "Browse our collection of designer Indian outfits for hire in Melbourne. Lehengas, sarees, jewellery & accessories.";
  const catalogRawTitle = `${seoTitle} Rental & Hire Melbourne | Indian Outfits – Mahra`;

  return (
    <div className="min-h-screen bg-[#FAF8F0] relative">
      <SEO
        rawTitle={catalogRawTitle}
        description={seoDesc}
        path={`/${parentSlug || "catalog"}${childSlug ? `/${childSlug}` : ""}`}
        breadcrumbs={[
          { name: "Home", url: "/" },
          ...(parent ? [{ name: parent.name, url: `/${parent.slug || parent.id}` }] : []),
          ...(child ? [{ name: child.name }] : []),
        ]}
      />
      <div className="petal-left hidden lg:block" />
      <div className="petal-right hidden lg:block" />
      <Navbar />
      <TierDiscountStrip />

      {/* Build Look Mode Banner */}
      {inBuildLookMode && (
        <div className="bg-[#9A7B4F] text-white py-2.5 px-4" data-testid="build-look-mode-banner">
          <div className="max-w-[1400px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[11px] tracking-[0.1em] uppercase font-medium">
                Building Your Look — Select {slotParam === 'outfit' ? 'an outfit' : slotParam === 'jewellery' ? 'jewellery' : slotParam === 'clutch' ? 'clutches & potlis' : 'an accessory'}
              </span>
            </div>
            <button
              onClick={() => navigate("/build-your-look")}
              className="text-[10px] tracking-[0.1em] uppercase underline underline-offset-2 hover:no-underline"
            >
              Back to Build Your Look
            </button>
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-2 sm:py-4 lg:py-8 relative">

        {/* Breadcrumb - desktop only */}
        <nav className="hidden sm:flex items-center gap-1.5 text-[12px] mb-2 sm:mb-6" data-testid="breadcrumb">
          <button onClick={() => navigate(-1)} className="w-7 h-7 flex items-center justify-center text-[#676664]/50 hover:text-[#282828] hover:bg-[#282828]/5 rounded transition-colors mr-1" data-testid="back-btn" aria-label="Go back">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <Link to="/" className="text-[#676664]/50 hover:text-[#676664] transition-colors">Home</Link>
          {parent && (
            <>
              <ChevronRight className="w-3 h-3 text-[#676664]/30" />
              <Link to={buildCategoryUrl(parent)} className={`transition-colors ${child ? "text-[#676664]/50 hover:text-[#676664]" : "text-[#282828] font-medium"}`}>{parent.name}</Link>
            </>
          )}
          {child && (
            <>
              <ChevronRight className="w-3 h-3 text-[#676664]/30" />
              <span className="text-[#282828] font-medium">{child.name}</span>
            </>
          )}
          {!parent && !child && (
            <>
              <ChevronRight className="w-3 h-3 text-[#676664]/30" />
              <span className="text-[#282828] font-medium">All</span>
            </>
          )}
        </nav>

        {/* Page Title — with back arrow inline on mobile */}
        <div className="flex items-center gap-2 mb-1 sm:mb-6 lg:mb-8">
          <button onClick={() => navigate(-1)} className="sm:hidden w-7 h-7 flex items-center justify-center text-[#676664]/50 hover:text-[#282828] hover:bg-[#282828]/5 rounded transition-colors" data-testid="mobile-back-btn" aria-label="Go back">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <h1 className="text-xl sm:text-3xl lg:text-4xl text-[#282828] font-light" data-testid="catalog-title">{pageTitle}</h1>
        </div>

        {/* Horizontal Filter Tabs */}
        {filterTabs.length > 0 && (
          <div className="flex items-center gap-6 mb-2 sm:mb-6 border-b border-[#282828]/10 overflow-x-auto" style={{ scrollbarWidth: "none" }} data-testid="filter-tabs">
            <Link to={buildCategoryUrl(parent)} className={`pb-3 text-[13px] whitespace-nowrap border-b-2 transition-colors ${activeFilter === "all" ? "border-[#282828] text-[#282828] font-medium" : "border-transparent text-[#676664]/60 hover:text-[#676664]"}`} data-testid="filter-tab-all">All</Link>
            {filterTabs.map(sub => (
              <Link key={sub.id} to={buildCategoryUrl(parent, sub)} className={`pb-3 text-[13px] whitespace-nowrap border-b-2 transition-colors ${activeFilter === (sub.slug || sub.id) ? "border-[#282828] text-[#282828] font-medium" : "border-transparent text-[#676664]/60 hover:text-[#676664]"}`} data-testid={`filter-tab-${sub.slug || sub.id}`}>{sub.name}</Link>
            ))}
          </div>
        )}

        {/* Persistent Date Bar — below filter tabs, above product grid */}
        <PersistentDateBar />

        {/* Main content with sidebar */}
        <div className="flex gap-8">
          {/* Desktop Filter Sidebar */}
          {(availableColours.length > 0 || availableEventTypes.length > 0) && (
            <div className="hidden lg:block w-52 flex-shrink-0" data-testid="filter-sidebar">
              <FilterPanel />
            </div>
          )}

          {/* Products area */}
          <div className="flex-1 min-w-0">
            {/* Search, Count, Mobile Filter Toggle */}
            <div className="flex items-center justify-between gap-4 mb-2 sm:mb-6">
              <div className="flex items-center gap-3">
                <p className="text-[11px] text-[#676664]/40">{filteredOutfits.length} {filteredOutfits.length === 1 ? "piece" : "pieces"}</p>
                {hasActiveFilters && (
                  <span className="text-[10px] text-[#9A7B4F] bg-[#9A7B4F]/10 px-2 py-0.5">
                    {selectedColours.length + selectedEventTypes.length} filter{selectedColours.length + selectedEventTypes.length > 1 ? 's' : ''} applied
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Mobile filter toggle — icon only on mobile */}
                {(availableColours.length > 0 || availableEventTypes.length > 0) && (
                  <button onClick={() => setShowMobileFilters(true)} className="lg:hidden flex items-center gap-1.5 text-[12px] text-[#5A5A5A] border border-[#282828]/10 px-2 py-1.5 sm:px-3" data-testid="mobile-filter-btn">
                    <SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span className="hidden sm:inline">Filters</span>
                  </button>
                )}
                {/* Search — icon-only toggle on mobile, full input on desktop */}
                <button onClick={() => setShowMobileSearch(s => !s)} className="sm:hidden flex items-center text-[#5A5A5A] border border-[#282828]/10 px-2 py-1.5" data-testid="mobile-search-btn">
                  <Search className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                <div className="hidden sm:block relative w-48">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-[#676664]/30" strokeWidth={1.5} />
                  <Input data-testid="search-input" type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 py-2 border-[#282828]/10 bg-white text-[12px]" />
                </div>
              </div>
            </div>
            {/* Mobile search expanded */}
            {showMobileSearch && (
              <div className="sm:hidden mb-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-[#676664]/30" strokeWidth={1.5} />
                  <Input data-testid="mobile-search-input" type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 py-2 border-[#282828]/10 bg-white text-[12px] w-full" autoFocus />
                </div>
              </div>
            )}

            {/* Product Grid */}
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 lg:gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-[#e8e4df] relative overflow-hidden" style={{ height: 0, paddingBottom: "133.33%" }}>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent skeleton-shimmer" />
                    </div>
                    <div className="pt-3 space-y-2">
                      <div className="h-3.5 bg-[#e8e4df] rounded w-3/4" />
                      <div className="h-3 bg-[#e8e4df] rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredOutfits.length > 0 ? (
              <>
              {/* Customer-facing Sort by — shared across all pages using Catalog */}
              <div className="flex items-center justify-end gap-2 mb-4 text-[11px] text-[#676664]">
                <label htmlFor="catalog-sort" className="uppercase tracking-wider text-[10px] text-[#676664]/60">Sort by</label>
                <select id="catalog-sort" value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="border border-[#282828]/15 px-2 py-1 text-[11px] bg-white focus:outline-none focus:border-[#9A7B4F]"
                  data-testid="catalog-sort-by">
                  <option value="featured">Featured / Recommended</option>
                  <option value="newest">Newest</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="name_asc">Name: A–Z</option>
                  <option value="name_desc">Name: Z–A</option>
                </select>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 lg:gap-6">
                {sortedOutfits.map((outfit, index) => (
                  <div key={outfit.id} className={`animate-fadeIn stagger-${Math.min(index + 1, 5)}`}>
                    <ProductCard 
                      outfit={outfit} 
                      availability={availability[outfit.id]} 
                      hasDate={hasDateSelected} 
                      hasDuration={rentalDuration !== null}
                      onQuickView={setQuickViewOutfit}
                      buildLookMode={inBuildLookMode}
                      buildLookSlot={slotParam}
                    />
                  </div>
                ))}
              </div>
              </>
            ) : (() => {
              // Determine if this is an empty category (no search/filters active) vs filtered-out results
              const isEmptyCategory = parent && !searchTerm && selectedColours.length === 0 && selectedEventTypes.length === 0 && !hasDateSelected;
              
              if (isEmptyCategory) {
                // Generate playful Coming Soon copy based on category name
                const catName = pageTitle || "this collection";
                const comingSoonCopy = {
                  default: { line1: `We're curating something special`, line2: `Our ${catName} collection is on its way. Stay tuned for pieces that will make every occasion unforgettable.` },
                  "for him": { line1: `Gentlemen, your wardrobe awaits`, line2: `We're putting together a distinguished collection just for him. Sharp sherwanis, kurtas and more — coming very soon.` },
                  "for kids": { line1: `Little ones, big style`, line2: `We're curating the most adorable outfits for your little stars. Tiny lehengas, kurtas and more — coming soon.` },
                  "for her": { line1: `Something beautiful is on the way`, line2: `We're adding fresh pieces to our collection. Check back soon for new arrivals.` },
                  "men": { line1: `Gentlemen, your wardrobe awaits`, line2: `We're assembling a refined collection of sherwanis, kurtas and Indo-western pieces. Stay tuned.` },
                  "kids": { line1: `Little fashionistas incoming`, line2: `Adorable outfits for your little ones are being curated with love. Watch this space.` },
                  "boys": { line1: `Dapper looks for the young gents`, line2: `Stylish kurtas, sherwanis and festive fits for boys — dropping soon.` },
                  "girls": { line1: `Tiny twirls, big moments`, line2: `Beautiful lehengas and outfits for little girls are on their way. Stay tuned.` },
                };
                const key = catName.toLowerCase();
                const copy = comingSoonCopy[key] || comingSoonCopy.default;

                return (
                  <div className="text-center py-20 lg:py-28 max-w-lg mx-auto" data-testid="coming-soon-category">
                    <div className="w-16 h-16 border border-[#9A7B4F]/20 flex items-center justify-center mx-auto mb-8 rotate-3">
                      <span className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>...</span>
                    </div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#9A7B4F] mb-4">Coming Soon</p>
                    <h3 className="text-2xl lg:text-3xl text-[#282828] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                      {copy.line1}
                    </h3>
                    <p className="text-[14px] text-[#676664]/70 font-light leading-relaxed mb-10">
                      {copy.line2}
                    </p>
                    <Link to="/" className="inline-block text-[11px] tracking-[0.15em] uppercase text-[#9A7B4F] border-b border-[#9A7B4F]/30 pb-0.5 hover:border-[#9A7B4F] transition-colors">
                      Browse Available Collection
                    </Link>
                  </div>
                );
              }

              return (
                <div className="text-center py-24">
                  <div className="w-20 h-20 border border-[#282828]/10 flex items-center justify-center mx-auto mb-6">
                    <Search className="w-8 h-8 text-[#676664]/20" strokeWidth={1} />
                  </div>
                  <h3 className="text-xl text-[#676664] mb-2">No pieces found</h3>
                  <p className="text-[#676664]/50 mb-8 text-sm font-light">Try adjusting your search or filters</p>
                  <Button onClick={clearAllFilters} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-8 text-[11px] tracking-[0.15em] uppercase">
                    Clear Filters
                  </Button>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Delivery info */}
        <div className="flex items-center justify-center gap-6 mt-14 pt-6 border-t border-[#282828]/5">
          <div className="flex items-center gap-2 text-[10px] text-[#676664]/50">
            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span>Free Local Pickup — Oakleigh South</span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-[#676664]/50">
            <Truck className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span>$15 Local Delivery — Melbourne</span>
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowMobileFilters(false)} onTouchEnd={(e) => { e.preventDefault(); setShowMobileFilters(false); }} />
          <div className="absolute right-0 top-0 bottom-0 w-[300px] bg-white shadow-xl animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#282828]/10">
              <h3 className="text-[14px] text-[#282828] font-medium">Filters</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setShowMobileFilters(false); }}
                className="w-11 h-11 flex items-center justify-center text-[#676664] hover:text-[#282828] -mr-2"
                data-testid="mobile-filter-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(100vh-60px)]">
              <FilterPanel isMobile />
            </div>
          </div>
        </div>
      )}

      <Footer />
      <QuickView outfit={quickViewOutfit} isOpen={!!quickViewOutfit} onClose={() => setQuickViewOutfit(null)} />
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-40 w-10 h-10 bg-[#9A7B4F] text-white shadow-lg flex items-center justify-center hover:bg-[#8B6B3F] transition-all animate-in fade-in duration-300"
          data-testid="back-to-top-btn"
        >
          <ArrowUp className="w-4 h-4" strokeWidth={2} />
        </button>
      )}
    </div>
  );
}
